import React, { useState, useEffect } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { extras, light, fontStack } from "styles/tokens";
import { setCredentials } from "state/authSlice";
import { useLogout } from "state/hooks/useLogout";
import {
  useWebAuthn,
  checkBiometricSupport,
  markEnrolledHere,
  guessDeviceLabel,
} from "state/hooks/useWebAuthn";
import { createLogger, summarizeError } from "../utils/logger";
import "./Login.css";

const log = createLogger("ui.SetInitialPassword");

// Mirrors Login's CSS-variable wiring so the bootstrap screen visually
// belongs to the same auth flow.
const LOGIN_VARS = {
  "--lg-font": fontStack,
  "--lg-surface": light.surface,
  "--lg-text-high": light.textHigh,
  "--lg-text-mid": light.textMid,
  "--lg-border": light.border,
  "--lg-border-strong": light.borderStrong,
  "--lg-accent": extras.accent.main,
  "--lg-accent-hover": extras.accent.hover,
  "--lg-accent-contrast": extras.accent.contrastText,
  "--lg-teal-deep": extras.surface.base,
  "--lg-teal-raised": extras.surface.raised,
  "--lg-danger": extras.feedback.danger,
  "--lg-danger-soft": extras.feedback.dangerSoft,
  "--lg-radius": `${extras.radius.lg}px`,
  "--lg-radius-btn": `${extras.radius.md}px`,
  "--lg-shadow": extras.shadow.lg,
  "--lg-focus": extras.shadow.focusRing,
};

function SetInitialPassword() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { logout } = useLogout();
  const {
    enrollPasskey,
    error: bioError,
    isLoading: bioLoading,
  } = useWebAuthn();
  const token = useSelector((state) => state.auth.token);
  const user = useSelector((state) => state.auth.user);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // After the password is set, offer to turn on Face ID on this device so the
  // whole first sign-in is one flow. Only where a platform authenticator exists.
  const [bioSupported, setBioSupported] = useState(false);
  const [showBioStep, setShowBioStep] = useState(false);

  useEffect(() => {
    checkBiometricSupport().then(setBioSupported);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}usersServer/me/setInitialPassword`,
        { password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { user: returnedUser, token: returnedToken } = res.data;
      localStorage.setItem(
        "user",
        JSON.stringify({ user: returnedUser, token: returnedToken })
      );
      dispatch(setCredentials({ user: returnedUser, token: returnedToken }));
      // Offer biometric setup as the final step of first sign-in. The store now
      // holds the full-scope token, so the enroll call on the next screen is
      // authed and past the must-set-password gate.
      if (bioSupported) {
        setShowBioStep(true);
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      log.error("set_initial.failed", { ...summarizeError(err) });
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to set password."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleEnableBio = async () => {
    const ok = await enrollPasskey(guessDeviceLabel());
    if (ok) {
      markEnrolledHere(user?._id);
      navigate("/dashboard");
    }
    // If the user dismissed the prompt, bioError shows; they can retry or skip
    // — they're already signed in either way.
  };

  // Final step of first sign-in: offer Face ID enrollment on this device.
  if (showBioStep) {
    return (
      <div className="login-root" style={LOGIN_VARS}>
        <aside className="login-brand" aria-hidden="true">
          <div className="login-brand__inner">
            <h1 className="login-brand__wordmark">Phieg CRM</h1>
            <p className="login-brand__tagline">You're all set.</p>
          </div>
          <div className="login-brand__glow" />
        </aside>

        <main className="login-form-pane">
          <div className="login-card">
            <h2 className="login-card__title">Enable Face ID?</h2>
            <p className="login-card__subtitle">
              Your password is set. Turn on Face ID / Touch ID / fingerprint to
              sign in on this device without typing your password next time —
              your password still works too.
            </p>

            {bioError && (
              <div className="login-alert" role="alert">
                <span className="login-alert__icon" aria-hidden="true">
                  !
                </span>
                <span>{bioError}</span>
              </div>
            )}

            <button
              type="button"
              className="login-submit"
              onClick={handleEnableBio}
              disabled={bioLoading}
            >
              {bioLoading
                ? "Waiting for device…"
                : "Enable Face ID on this device"}
            </button>

            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              style={{
                marginTop: 12,
                background: "none",
                border: 0,
                color: "var(--lg-text-mid)",
                fontSize: "0.85rem",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Skip for now
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="login-root" style={LOGIN_VARS}>
      <aside className="login-brand" aria-hidden="true">
        <div className="login-brand__inner">
          <h1 className="login-brand__wordmark">Phieg CRM</h1>
          <p className="login-brand__tagline">
            One last step before you're in.
          </p>
        </div>
        <div className="login-brand__glow" />
      </aside>

      <main className="login-form-pane">
        <div className="login-card">
          <h2 className="login-card__title">Set your password</h2>
          <p className="login-card__subtitle">
            Welcome{user?.name ? `, ${user.name}` : ""}. Pick a password you'll
            use to sign in from now on.
          </p>

          {error && (
            <div className="login-alert" role="alert">
              <span className="login-alert__icon" aria-hidden="true">
                !
              </span>
              <span>{error}</span>
            </div>
          )}

          <form className="login-form" onSubmit={handleSubmit}>
            <label className="login-field">
              <span className="login-field__label">New password</span>
              <div className="login-input-wrap">
                <input
                  className="login-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  placeholder="At least 6 characters"
                  required
                />
                <button
                  type="button"
                  className="login-reveal"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </label>

            <label className="login-field">
              <span className="login-field__label">Confirm password</span>
              <input
                className="login-input"
                type={showPassword ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                placeholder="Re-enter password"
                required
              />
            </label>

            <button
              type="submit"
              className="login-submit"
              disabled={submitting}
            >
              {submitting ? "Saving…" : "Save password"}
            </button>
          </form>

          <button
            type="button"
            onClick={logout}
            style={{
              marginTop: 12,
              background: "none",
              border: 0,
              color: "var(--lg-text-mid)",
              fontSize: "0.85rem",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Sign out
          </button>
        </div>
      </main>
    </div>
  );
}

export default SetInitialPassword;
